import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  ComponentRef,
  OnDestroy,
  EmbeddedViewRef,
  ElementRef
} from '@angular/core';
import { Observable, Subscription, isObservable } from 'rxjs';
import { IsLoadingSpinnerComponent } from './is-loading-spinner.component';

/**
 * Structural directive that shows a loading spinner or custom template while data is loading.
 * 
 * @example
 * <!-- Basic usage with boolean -->
 * <div *isLoading="isDataLoading">
 *   {{ data }}
 * </div>
 * 
 * @example
 * <!-- With Observable -->
 * <div *isLoading="loading$">
 *   {{ data }}
 * </div>
 * 
 * @example
 * <!-- With loading message -->
 * <div *isLoading="loading$; message: 'Loading data...'">
 *   {{ data }}
 * </div>
 * 
 * @example
 * <!-- With custom spinner template -->
 * <div *isLoading="loading$; spinner: customSpinner">
 *   {{ data }}
 * </div>
 * <ng-template #customSpinner>
 *   <p>Loading...</p>
 * </ng-template>
 */
@Directive({
  selector: '[isLoading]'
})
export class IsLoadingDirective implements OnDestroy {
  private loadingSub?: Subscription;
  private currentLoading: boolean | null = null;
  private spinnerRef?: ComponentRef<IsLoadingSpinnerComponent> | EmbeddedViewRef<any>;

  /**
   * Controls the loading state. Can be a boolean or an Observable<boolean>.
   */
  @Input()
  set isLoading(value: boolean | Observable<boolean>) {
    this.handleLoadingChange(value);
  }

  /**
   * Optional custom spinner template.
   * Use as: *isLoading="loading$; spinner: myTemplate"
   */
  @Input('isLoadingSpinner') spinnerTemplate?: TemplateRef<any>;

  /**
   * Optional message to display below the spinner.
   * Use as: *isLoading="loading$; message: 'Please wait...'"
   */
  @Input('isLoadingMessage') 
  set message(value: string | undefined) {
    this._message = value;
    // If spinner is already showing, update its message
    if (this.currentLoading && this.spinnerRef && 'instance' in this.spinnerRef) {
      this.spinnerRef.instance.message = value;
    }
  }
  get message(): string | undefined {
    return this._message;
  }
  private _message?: string;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  private handleLoadingChange(value: boolean | Observable<boolean>): void {
    // Unsubscribe from previous observable if any
    this.loadingSub?.unsubscribe();
    this.loadingSub = undefined;

    if (isObservable(value)) {
      this.loadingSub = value.subscribe(loading => this.updateView(!!loading));
    } else {
      this.updateView(!!value);
    }
  }

  private updateView(loading: boolean): void {
    // Skip update if state hasn't changed
    if (loading === this.currentLoading) {
      return;
    }

    this.currentLoading = loading;
    this.updateAriaBusy(loading);
    
    // Clear current view
    this.viewContainer.clear();
    this.spinnerRef = undefined;

    if (loading) {
      this.showLoadingSpinner();
    } else {
      this.showContent();
    }
  }

  private showLoadingSpinner(): void {
    if (this.spinnerTemplate) {
      // Use custom spinner template if provided
      this.spinnerRef = this.viewContainer.createEmbeddedView(this.spinnerTemplate);
    } else {
      // Create default spinner component (Angular Ivy syntax)
      this.spinnerRef = this.viewContainer.createComponent(IsLoadingSpinnerComponent);
      
      // Pass message to spinner component if provided
      if (this._message) {
        this.spinnerRef.instance.message = this._message;
      }
    }
  }

  private showContent(): void {
    // Show the original content
    this.viewContainer.createEmbeddedView(this.templateRef);
  }

  private updateAriaBusy(loading: boolean): void {
    // Get the parent element of the structural directive's anchor comment
    const hostElement = this.elementRef.nativeElement.parentElement;
    
    if (!hostElement) {
      return;
    }

    if (loading) {
      hostElement.setAttribute('aria-busy', 'true');
    } else {
      hostElement.removeAttribute('aria-busy');
    }
  }

  ngOnDestroy(): void {
    // Clean up subscription
    this.loadingSub?.unsubscribe();
    
    // Ensure aria-busy is removed if directive is destroyed while loading
    this.updateAriaBusy(false);
    
    // Clear any remaining views
    this.viewContainer.clear();
    this.spinnerRef = undefined;
  }
}